import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Pie } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend
} from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';

// Register ChartJS components
ChartJS.register(ArcElement, Tooltip, Legend, ChartDataLabels);

/**
 * TransactionTrendChart Component
 * 
 * Displays a pie chart showing transaction trends by category
 * with small text labels directly on segments
 * 
 * @param {Object} props Component props
 * @param {Array} props.transactionTrends Array of transaction trends data
 * @param {boolean} props.isLoading Loading state indicator
 * @returns {JSX.Element} TransactionTrendChart component
 */
const TransactionTrendChart = ({ transactionTrends = [], isLoading = false }) => {
  console.log('TransactionTrendChart - Received trends data:', transactionTrends);
  const navigate = useNavigate();
  
  if (isLoading) {
    return (
      <div className="animate-pulse">
        <div className="h-64 bg-gray-200 rounded"></div>
      </div>
    );
  }

  // Strict checking for empty or invalid data
  if (!transactionTrends || 
      !Array.isArray(transactionTrends) || 
      transactionTrends.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-64 bg-gray-50 rounded-lg border border-gray-200">
        <p className="text-gray-500 mb-2">No transaction data available</p>
        <p className="text-sm text-gray-400">Add transactions to see spending trends</p>
      </div>
    );
  }

  // Normalize data to handle different property names
  const normalizedData = transactionTrends.map(item => ({
    category: item.category || item.name || 'Unknown',
    amount: typeof item.amount === 'number' ? item.amount : 0,
    percentage: typeof item.percentage === 'number' ? item.percentage : 0
  }));

  // Filter out any items with zero amount
  const filteredData = normalizedData.filter(item => item.amount > 0);
  
  if (filteredData.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-64 bg-gray-50 rounded-lg border border-gray-200">
        <p className="text-gray-500 mb-2">No transaction data to display</p>
        <p className="text-sm text-gray-400">Add transactions to see spending trends</p>
      </div>
    );
  }

  // Additional check - if all amounts are zero, show empty state
  const totalAmount = filteredData.reduce((sum, item) => sum + (item.amount || 0), 0);
  if (totalAmount === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-64 bg-gray-50 rounded-lg border border-gray-200">
        <p className="text-gray-500 mb-2">No spending data found</p>
        <p className="text-sm text-gray-400">Add transactions with amounts to see trends</p>
      </div>
    );
  }

  const chartColors = [
    'rgba(99, 102, 241, 0.8)',   // Indigo (Bills)
    'rgba(236, 72, 153, 0.8)',   // Pink (Shopping)
    'rgba(251, 146, 60, 0.8)',   // Orange (Groceries)
    'rgba(14, 165, 233, 0.8)',   // Sky (Entertainment)
    'rgba(139, 92, 246, 0.8)',   // Purple
    'rgba(34, 197, 94, 0.8)',    // Green
    'rgba(245, 158, 11, 0.8)',   // Amber
    'rgba(249, 115, 22, 0.8)',   // Orange
    'rgba(239, 68, 68, 0.8)',    // Red
    'rgba(16, 185, 129, 0.8)',   // Emerald
  ];

  const chartData = {
    labels: filteredData.map(item => item.category),
    datasets: [
      {
        data: filteredData.map(item => item.amount),
        backgroundColor: chartColors.slice(0, filteredData.length),
        borderColor: 'white',
        borderWidth: 2,
      }
    ]
  };

  // Handle chart click to navigate to transactions filtered by category
  const handleChartClick = (event, elements) => {
    if (elements.length > 0) {
      const elementIndex = elements[0].index;
      const categoryName = filteredData[elementIndex].category;
      // Navigate to transactions page with category filter
      navigate(`/transactions?category=${encodeURIComponent(categoryName)}`);
    }
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    onClick: handleChartClick,
    plugins: {
      legend: {
        position: 'bottom',
        labels: {
          padding: 20,
          color: '#374151', // Gray-700
          font: {
            size: 12
          },
          boxWidth: 15
        }
      },
      tooltip: {
        backgroundColor: 'rgba(255, 255, 255, 0.9)',
        titleColor: '#374151',
        bodyColor: '#374151',
        borderColor: '#E5E7EB',
        borderWidth: 1,
        padding: 12,
        callbacks: {
          label: (context) => {
            const value = context.raw;
            const total = context.dataset.data.reduce((a, b) => a + b, 0);
            const percentage = ((value / total) * 100).toFixed(1);
            return `${context.label}: $${value.toLocaleString()} (${percentage}%)`;
          }
        }
      },
      datalabels: {
        display: function(context) {
          // Only show labels for slices that are large enough (> 8% of total)
          const value = context.parsed;
          const total = context.dataset.data.reduce((a, b) => a + b, 0);
          const percentage = (value / total) * 100;
          return percentage > 8;
        },
        color: 'white',
        font: {
          size: 10,
          weight: 'bold'
        },
        anchor: 'center',
        align: 'center',
        formatter: (value, context) => {
          const total = context.dataset.data.reduce((a, b) => a + b, 0);
          const percentage = ((value / total) * 100).toFixed(1);
          // For small segments, show only percentage
          if (percentage < 15) {
            return `${percentage}%`;
          }
          // For larger segments, show both amount and percentage
          return `$${value.toLocaleString()}\n(${percentage}%)`;
        },
        textStrokeColor: 'rgba(0, 0, 0, 0.3)',
        textStrokeWidth: 1
      }
    },
    radius: '90%'
  };

  return (
    <div className="h-full flex flex-col">
      <div className="flex-1 min-h-0">
        <Pie data={chartData} options={options} plugins={[ChartDataLabels]} />
      </div>
      
      {/* Data Table */}
      <div className="mt-4 border-t pt-4">
        <div className="max-h-32 overflow-y-auto">
          <table className="w-3/4 mx-auto text-xs">
            <thead>
              <tr className="bg-gray-50">
                <th className="text-left px-2 py-1 font-medium text-gray-600">Category</th>
                <th className="text-right px-2 py-1 font-medium text-gray-600">Amount</th>
              </tr>
            </thead>
            <tbody>
              {filteredData.map((item, index) => (
                <tr key={index} className="border-t border-gray-100">
                  <td className="px-2 py-1 text-gray-700">{item.category}</td>
                  <td className="px-2 py-1 text-right text-gray-700">
                    ${item.amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default TransactionTrendChart; 